import { useState } from 'react';
import ProgressBar from '../shared/ProgressBar';
import { ChevronLeft } from '../shared/icons';

const TestStep = ({ testType, currentQuestion, totalQuestions, questionData, onAnswer, onBack }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [checkboxSelections, setCheckboxSelections] = useState([]);
  const [yesNoMultiSelections, setYesNoMultiSelections] = useState([]);
  // ASSIST-specific state
  const [assistStep, setAssistStep] = useState('substances'); // 'substances' | 'followup'
  const [assistSubstances, setAssistSubstances] = useState({});
  const [assistResponses, setAssistResponses] = useState({});
  const [assistCurrentSubstance, setAssistCurrentSubstance] = useState(0);
  const [assistCurrentQ, setAssistCurrentQ] = useState('q2');

  // Determine question type (default to multiple_choice for backward compatibility)
  const questionType = questionData.questionType || 'multiple_choice';

  // Handle multiple choice (standard - BDI, BAI, YBOCS)
  const handleMultipleChoiceClick = (idx) => {
    setSelectedOption(idx);
    setTimeout(() => {
      setSelectedOption(null);
      onAnswer(idx);
    }, 400);
  };

  // Handle yes/no questions (MDQ Q2, Q4, Q5)
  const handleYesNoClick = (value) => {
    setSelectedOption(value);
    setTimeout(() => {
      setSelectedOption(null);
      onAnswer(value); // 1 for yes, 0 for no
    }, 400);
  };

  // Handle yes/no multi (MDQ Q1 with 13 sub-questions)
  const toggleYesNoMulti = (idx) => {
    const newSelections = [...yesNoMultiSelections];
    newSelections[idx] = newSelections[idx] === 1 ? 0 : 1;
    setYesNoMultiSelections(newSelections);
  };

  const submitYesNoMulti = () => {
    // Ensure all 13 items have a value (default to 0 if not checked)
    const answers = questionData.subQuestions.map((_, idx) => yesNoMultiSelections[idx] || 0);
    onAnswer(answers);
  };

  // Handle checkbox questions (BSDS Q1-19)
  const toggleCheckbox = (idx) => {
    const newSelections = [...checkboxSelections];
    newSelections[idx] = !newSelections[idx];
    setCheckboxSelections(newSelections);
  };

  const submitCheckboxes = () => {
    // For checkbox questions, return 1 if checked, 0 if not
    const isChecked = checkboxSelections[0] || false;
    onAnswer(isChecked ? 1 : 0);
  };

  // Handle likert / clinician_rating (store point value, not index)
  const handleLikertClick = (points) => {
    setSelectedOption(points);
    setTimeout(() => {
      setSelectedOption(null);
      onAnswer(points);
    }, 400);
  };

  // Handle gate yes/no (PC-PTSD-5 gate question)
  const handleGateYesNoClick = (value) => {
    setSelectedOption(value);
    setTimeout(() => {
      setSelectedOption(null);
      onAnswer(value); // 1 for yes, 0 for no
    }, 400);
  };

  // Handle weighted multiple choice (BSDS Q20)
  const handleWeightedMultipleChoice = (idx) => {
    setSelectedOption(idx);
    setTimeout(() => {
      setSelectedOption(null);
      onAnswer(idx); // Index will be used to look up points in scoring
    }, 400);
  };

  // Render based on question type
  const renderQuestionContent = () => {
    // Multiple choice (standard)
    if (questionType === 'multiple_choice') {
      const options = questionData.options || [];
      const promptText = testType === 'BDI'
        ? "Select the statement that best describes how you have been feeling:"
        : testType === 'BAI'
        ? `How much have you been bothered by "${questionData.symptom}"?`
        : questionData.text || "Select an option:";

      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-8 sm:mb-10 leading-tight">
            {promptText}
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleMultipleChoiceClick(idx)}
                disabled={selectedOption !== null}
                className={`option-card w-full text-left p-4 sm:p-5 rounded-xl border-2 group flex items-center gap-3 sm:gap-4 transition-all duration-300 relative overflow-hidden ${
                  selectedOption === idx
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl scale-[1.02]'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50/50 shadow-sm text-brand-neutral-700 hover:text-blue-800 hover:shadow-xl'
                }`}
              >
                <div className="absolute inset-0 bg-blue-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className={`relative z-10 flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm sm:text-base transition-all duration-300 ${
                  selectedOption === idx
                    ? 'bg-white text-blue-800 shadow-lg scale-110'
                    : 'bg-blue-100/80 text-blue-800 group-hover:bg-blue-800 group-hover:text-white group-hover:shadow-md group-hover:scale-110'
                }`}>
                  {String.fromCharCode(65 + idx)}
                </div>
                <span className={`relative z-10 text-sm sm:text-base font-medium leading-relaxed transition-all duration-300 ${
                  selectedOption === idx ? 'font-bold' : 'group-hover:font-semibold'
                }`}>
                  {opt}
                </span>
                {selectedOption === idx && (
                  <div className="ml-auto relative z-10">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 animate-bounce-in" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Yes/No questions (MDQ Q2, Q4, Q5)
    if (questionType === 'yes_no') {
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-8 sm:mb-10 leading-tight">
            {questionData.text}
          </h2>
          <div className="flex gap-4">
            {[
              { label: 'Yes', value: 1 },
              { label: 'No', value: 0 }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleYesNoClick(option.value)}
                disabled={selectedOption !== null}
                className={`flex-1 p-6 rounded-xl border-2 font-bold text-lg transition-all duration-300 ${
                  selectedOption === option.value
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl scale-[1.02]'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50 shadow-sm text-brand-neutral-700 hover:text-blue-800'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Yes/No multi (MDQ Q1 with 13 sub-questions)
    if (questionType === 'yes_no_multi') {
      const subQuestions = questionData.subQuestions || [];
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-6 leading-tight">
            {questionData.text}
          </h2>
          <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto pr-2">
            {subQuestions.map((subQ, idx) => (
              <label
                key={idx}
                className="flex items-start gap-3 p-4 rounded-lg bg-white border-2 border-brand-neutral-200 hover:border-blue-400 cursor-pointer transition-all"
              >
                <input
                  type="checkbox"
                  checked={yesNoMultiSelections[idx] === 1}
                  onChange={() => toggleYesNoMulti(idx)}
                  className="mt-1 w-5 h-5 text-blue-800 rounded focus:ring-blue-500"
                />
                <span className="text-sm sm:text-base text-brand-neutral-700 leading-relaxed">
                  {subQ}
                </span>
              </label>
            ))}
          </div>
          <button
            onClick={submitYesNoMulti}
            className="w-full py-4 bg-blue-800 text-white rounded-xl font-semibold text-lg hover:bg-blue-900 transition-all shadow-lg"
          >
            Continue
          </button>
        </>
      );
    }

    // Scale questions (MDQ Q3)
    if (questionType === 'scale') {
      const options = questionData.options || [];
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-8 sm:mb-10 leading-tight">
            {questionData.text}
          </h2>
          <div className="space-y-3">
            {options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleMultipleChoiceClick(idx)}
                disabled={selectedOption !== null}
                className={`w-full p-5 rounded-xl border-2 font-semibold text-base transition-all duration-300 ${
                  selectedOption === idx
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50 text-brand-neutral-700 hover:text-blue-800'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Checkbox questions (BSDS Q1-19)
    if (questionType === 'checkbox') {
      return (
        <>
          <h2 className="text-lg sm:text-xl font-display font-semibold text-brand-navy-dark mb-6 leading-tight">
            Please check if this sentence definitely describes you:
          </h2>
          <label className="flex items-start gap-4 p-6 rounded-xl bg-white border-2 border-brand-neutral-200 hover:border-blue-400 cursor-pointer transition-all mb-6">
            <input
              type="checkbox"
              checked={checkboxSelections[0] || false}
              onChange={() => toggleCheckbox(0)}
              className="mt-1 w-6 h-6 text-blue-800 rounded focus:ring-blue-500"
            />
            <span className="text-base sm:text-lg text-brand-neutral-700 leading-relaxed">
              {questionData.text}
            </span>
          </label>
          <button
            onClick={submitCheckboxes}
            className="w-full py-4 bg-blue-800 text-white rounded-xl font-semibold text-lg hover:bg-blue-900 transition-all shadow-lg"
          >
            Continue
          </button>
        </>
      );
    }

    // Weighted multiple choice (BSDS Q20)
    if (questionType === 'multiple_choice_weighted') {
      const options = questionData.options || [];
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-8 sm:mb-10 leading-tight">
            {questionData.text}
          </h2>
          <div className="space-y-3">
            {options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleWeightedMultipleChoice(idx)}
                disabled={selectedOption !== null}
                className={`w-full p-5 rounded-xl border-2 text-left font-medium text-base transition-all duration-300 ${
                  selectedOption === idx
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50 text-brand-neutral-700 hover:text-blue-800'
                }`}
              >
                {opt.text}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Likert scale (all questions share the same scale, store point value)
    if (questionType === 'likert') {
      const options = questionData.options || [];
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-4 sm:mb-6 leading-tight">
            {questionData.text}
          </h2>
          {questionData.description && (
            <p className="text-sm sm:text-base text-brand-neutral-600 mb-6 sm:mb-8 leading-relaxed">
              {questionData.description}
            </p>
          )}
          <div className="space-y-3 sm:space-y-4">
            {options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleLikertClick(opt.points)}
                disabled={selectedOption !== null}
                className={`option-card w-full text-left p-4 sm:p-5 rounded-xl border-2 group flex items-center gap-3 sm:gap-4 transition-all duration-300 relative overflow-hidden ${
                  selectedOption === opt.points
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl scale-[1.02]'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50/50 shadow-sm text-brand-neutral-700 hover:text-blue-800 hover:shadow-xl'
                }`}
              >
                <div className="absolute inset-0 bg-blue-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className={`relative z-10 flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm sm:text-base transition-all duration-300 ${
                  selectedOption === opt.points
                    ? 'bg-white text-blue-800 shadow-lg scale-110'
                    : 'bg-blue-100/80 text-blue-800 group-hover:bg-blue-800 group-hover:text-white group-hover:shadow-md group-hover:scale-110'
                }`}>
                  {opt.points}
                </div>
                <span className={`relative z-10 text-sm sm:text-base font-medium leading-relaxed transition-all duration-300 ${
                  selectedOption === opt.points ? 'font-bold' : 'group-hover:font-semibold'
                }`}>
                  {opt.label}
                </span>
                {selectedOption === opt.points && (
                  <div className="ml-auto relative z-10">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 animate-bounce-in" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Clinician rating (longer descriptive anchors, scrollable)
    if (questionType === 'clinician_rating') {
      const options = questionData.options || [];
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-4 sm:mb-6 leading-tight">
            {questionData.text}
          </h2>
          {questionData.description && (
            <p className="text-sm sm:text-base text-brand-neutral-600 mb-6 sm:mb-8 leading-relaxed">
              {questionData.description}
            </p>
          )}
          <div className="space-y-3 sm:space-y-4 max-h-[450px] overflow-y-auto">
            {options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleLikertClick(opt.points)}
                disabled={selectedOption !== null}
                className={`option-card w-full text-left p-4 sm:p-5 rounded-xl border-2 group flex items-center gap-3 sm:gap-4 transition-all duration-300 relative overflow-hidden ${
                  selectedOption === opt.points
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl scale-[1.02]'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50/50 shadow-sm text-brand-neutral-700 hover:text-blue-800 hover:shadow-xl'
                }`}
              >
                <div className="absolute inset-0 bg-blue-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className={`relative z-10 flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-sm sm:text-base transition-all duration-300 ${
                  selectedOption === opt.points
                    ? 'bg-white text-blue-800 shadow-lg scale-110'
                    : 'bg-blue-100/80 text-blue-800 group-hover:bg-blue-800 group-hover:text-white group-hover:shadow-md group-hover:scale-110'
                }`}>
                  {opt.points}
                </div>
                <span className={`relative z-10 text-xs sm:text-sm font-medium leading-relaxed transition-all duration-300 ${
                  selectedOption === opt.points ? 'font-bold' : 'group-hover:font-semibold'
                }`}>
                  {opt.label}
                </span>
                {selectedOption === opt.points && (
                  <div className="ml-auto relative z-10">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 animate-bounce-in" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      );
    }

    // Gate yes/no (PC-PTSD-5 gate question)
    if (questionType === 'gate_yes_no') {
      return (
        <>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-brand-navy-dark mb-4 sm:mb-6 leading-tight">
            {questionData.text}
          </h2>
          {questionData.description && (
            <p className="text-sm sm:text-base text-brand-neutral-600 mb-6 sm:mb-8 leading-relaxed">
              {questionData.description}
            </p>
          )}
          <div className="flex gap-4">
            {[
              { label: 'Yes', value: 1 },
              { label: 'No', value: 0 }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleGateYesNoClick(option.value)}
                disabled={selectedOption !== null}
                className={`flex-1 p-6 rounded-xl border-2 font-bold text-lg transition-all duration-300 ${
                  selectedOption === option.value
                    ? 'bg-blue-800 border-blue-800 text-white shadow-2xl scale-[1.02]'
                    : 'bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50 shadow-sm text-brand-neutral-700 hover:text-blue-800'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      );
    }

    // ASSIST substance screening (multi-step within single question)
    if (questionType === 'assist_substance_screen') {
      const substances = questionData.substances || [];
      const followUpQs = questionData.followUpQuestions || {};
      const selectedSubs = Object.keys(assistSubstances).filter(k => assistSubstances[k]);
      const qOrder = ['q2', 'q3', 'q4', 'q5', 'q6', 'q7'];

      if (assistStep === 'substances') {
        return (
          <>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-brand-navy-dark mb-4 leading-tight">
              {questionData.text}
            </h2>
            <div className="space-y-2 mb-6 max-h-[400px] overflow-y-auto pr-2">
              {substances.map((sub, idx) => (
                <label key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-white border-2 border-brand-neutral-200 hover:border-blue-400 cursor-pointer transition-all">
                  <input
                    type="checkbox"
                    checked={assistSubstances[sub.name] || false}
                    onChange={() => setAssistSubstances(prev => ({ ...prev, [sub.name]: !prev[sub.name] }))}
                    className="mt-1 w-5 h-5 text-blue-800 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-brand-neutral-700">{sub.name}</span>
                    <span className="text-xs text-brand-neutral-400 ml-1">({sub.examples})</span>
                  </div>
                </label>
              ))}
            </div>
            <button
              onClick={() => {
                if (selectedSubs.length === 0) {
                  // No substances used: submit with empty result
                  onAnswer({ substancesUsed: assistSubstances, responses: {} });
                } else {
                  setAssistStep('followup');
                  setAssistCurrentSubstance(0);
                  setAssistCurrentQ('q2');
                }
              }}
              className="w-full py-4 bg-blue-800 text-white rounded-xl font-semibold text-lg hover:bg-blue-900 transition-all shadow-lg"
            >
              Continue
            </button>
          </>
        );
      }

      if (assistStep === 'followup') {
        const currentSub = selectedSubs[assistCurrentSubstance];
        const currentFollowUp = followUpQs[assistCurrentQ];
        if (!currentFollowUp) return null;

        // Skip Q5 for tobacco
        const isSkipped = assistCurrentQ === 'q5' && currentFollowUp.skipForTobacco && currentSub === 'Tobacco products';
        if (isSkipped) {
          // Auto-advance past skipped question
          const nextQIdx = qOrder.indexOf(assistCurrentQ) + 1;
          if (nextQIdx < qOrder.length) {
            setTimeout(() => setAssistCurrentQ(qOrder[nextQIdx]), 0);
          }
          return <p className="text-brand-neutral-500">Skipping...</p>;
        }

        const questionText = currentFollowUp.text.replace('{substance}', currentSub);

        return (
          <>
            <div className="mb-4">
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                {currentSub} ({assistCurrentSubstance + 1}/{selectedSubs.length})
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-brand-navy-dark mb-6 leading-tight">
              {questionText}
            </h2>
            <div className="space-y-2">
              {currentFollowUp.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    // Save response
                    setAssistResponses(prev => ({
                      ...prev,
                      [currentSub]: { ...(prev[currentSub] || {}), [assistCurrentQ]: opt.points }
                    }));

                    // Advance to next question or next substance or finish
                    const nextQIdx = qOrder.indexOf(assistCurrentQ) + 1;
                    // Find next valid question (skip Q5 for tobacco)
                    let nextQ = null;
                    for (let i = nextQIdx; i < qOrder.length; i++) {
                      const q = qOrder[i];
                      const qData = followUpQs[q];
                      if (q === 'q5' && qData?.skipForTobacco && currentSub === 'Tobacco products') continue;
                      nextQ = q;
                      break;
                    }

                    if (nextQ) {
                      setAssistCurrentQ(nextQ);
                    } else if (assistCurrentSubstance < selectedSubs.length - 1) {
                      setAssistCurrentSubstance(assistCurrentSubstance + 1);
                      setAssistCurrentQ('q2');
                    } else {
                      // All done, collect final responses including current selection
                      const finalResponses = {
                        ...assistResponses,
                        [currentSub]: { ...(assistResponses[currentSub] || {}), [assistCurrentQ]: opt.points }
                      };
                      onAnswer({ substancesUsed: assistSubstances, responses: finalResponses });
                    }
                  }}
                  className={`option-card w-full text-left p-3 sm:p-4 rounded-xl border-2 group flex items-center gap-3 transition-all duration-300 bg-white border-brand-neutral-200 hover:border-blue-400 hover:bg-blue-50/50 shadow-sm text-brand-neutral-700 hover:text-blue-800`}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm bg-blue-100/80 text-blue-800 group-hover:bg-blue-800 group-hover:text-white transition-all">
                    {opt.points}
                  </div>
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
          </>
        );
      }
    }

    return null;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-3xl animate-fade-in relative z-10">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="p-2 sm:p-3 rounded-xl hover:bg-white/80 text-brand-neutral-600 hover:text-brand-navy transition-all duration-200 flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-medium shadow-sm hover:shadow-md backdrop-blur-sm"
          >
            <ChevronLeft size={20} />
            <span className="hidden sm:inline">Back</span>
          </button>
          <div className="text-sm sm:text-base font-semibold text-brand-neutral-600 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
            <span className="text-brand-navy text-lg sm:text-xl font-display">{currentQuestion + 1}</span>
            <span className="text-brand-neutral-400 mx-1">/</span>
            <span className="text-brand-neutral-600">{totalQuestions}</span>
          </div>
        </div>

        <ProgressBar current={currentQuestion} total={totalQuestions} />

        <div className="glass-panel p-6 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl min-h-[400px] sm:min-h-[480px] flex flex-col justify-center">
          {renderQuestionContent()}
        </div>

        <div className="mt-6 text-center animate-fade-in-slow">
          <p className="text-sm text-brand-neutral-500 font-medium">
            Take your time
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestStep;
